import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  CalendarDays, Shirt, Smartphone, Wrench, Sparkles, Gift, Star,
} from 'lucide-react';
import { useT } from '../i18n/I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage keys for "new dot" detection (Eventi & Nuovi).
const STORAGE_EVENTS = 'kyloo_events_last_seen';
const STORAGE_EVENTS_LATEST = 'kyloo_events_latest';
const STORAGE_NUOVI = 'kyloo_nuovi_last_seen';
const STORAGE_NUOVI_LATEST = 'kyloo_nuovi_latest';

const PILLS = [
  { id: 'nuovi',       icon: Star,         color: 'text-orange-500', hasDot: 'nuovi' },
  { id: 'moda',        icon: Shirt,        color: 'text-pink-600' },
  { id: 'elettronica', icon: Smartphone,   color: 'text-blue-600' },
  { id: 'fai_da_te',   icon: Wrench,       color: 'text-amber-600' },
  { id: 'servizi',     icon: Sparkles,     color: 'text-emerald-600' },
  { id: 'idee_regalo', icon: Gift,         color: 'text-rose-600' },
  { id: 'eventi',      icon: CalendarDays, color: 'text-purple-600', hasDot: 'events' },
];

/** Returns true if storage's last seen timestamp is older than the latest fetched id. */
function shouldShowDot(storageKey, storageLatestKey, latestId) {
  if (!latestId) return false;
  try {
    const seen = localStorage.getItem(storageKey);
    const cached = localStorage.getItem(storageLatestKey);
    if (cached !== latestId) {
      localStorage.setItem(storageLatestKey, latestId);
      return seen !== latestId;
    }
    return seen !== latestId;
  } catch {
    return false;
  }
}

function markSeen(storageKey, storageLatestKey) {
  try {
    const latest = localStorage.getItem(storageLatestKey);
    if (latest) localStorage.setItem(storageKey, latest);
  } catch (e) {
    // localStorage disabled / privacy mode — dot state is best-effort.
    console.debug('markSeen storage failed:', e?.message);
  }
}

export default function CategoryPills({ selectedCategory, onSelect }) {
  const { t } = useT();
  const [eventsDot, setEventsDot] = useState(false);
  const [nuoviDot, setNuoviDot] = useState(false);

  const refreshDots = useCallback(async () => {
    try {
      const [evRes, nuRes] = await Promise.all([
        axios.get(`${API}/products?gruppo=eventi&limit=1`).catch(() => null),
        axios.get(`${API}/products?gruppo=nuovi&limit=1`).catch(() => null),
      ]);
      const evLatest = evRes?.data?.[0]?.id;
      const nuLatest = nuRes?.data?.[0]?.id;
      setEventsDot(shouldShowDot(STORAGE_EVENTS, STORAGE_EVENTS_LATEST, evLatest));
      setNuoviDot(shouldShowDot(STORAGE_NUOVI, STORAGE_NUOVI_LATEST, nuLatest));
    } catch (e) {
      console.debug('Category dots refresh failed:', e?.message);
    }
  }, []);

  useEffect(() => {
    refreshDots();
  }, [refreshDots]);

  const handleClick = (id) => {
    if (id === 'eventi') {
      markSeen(STORAGE_EVENTS, STORAGE_EVENTS_LATEST);
      setEventsDot(false);
    }
    if (id === 'nuovi') {
      markSeen(STORAGE_NUOVI, STORAGE_NUOVI_LATEST);
      setNuoviDot(false);
    }
    onSelect(selectedCategory === id ? null : id);
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" data-testid="category-pills">
      {PILLS.map((p) => {
        const Icon = p.icon;
        const isSelected = selectedCategory === p.id;
        const showDot =
          (p.hasDot === 'events' && eventsDot) ||
          (p.hasDot === 'nuovi' && nuoviDot);

        return (
          <button
            key={p.id}
            onClick={() => handleClick(p.id)}
            className={`relative flex flex-col items-center gap-1 px-4 py-2 rounded-xl whitespace-nowrap transition-all category-pill ${
              isSelected ? 'active' : 'bg-slate-100 hover:bg-slate-200'
            }`}
            data-testid={`category-${p.id}`}
          >
            <Icon className={`w-5 h-5 ${isSelected ? '' : p.color}`} />
            <span className="text-xs font-medium">{t(`categories.${p.id}`)}</span>
            {showDot && (
              <span
                className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse"
                data-testid={`category-dot-${p.id}`}
                aria-label={t('events.aria_new')}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
